import { useCallback, useState, type AnimationEvent, type ReactNode } from 'react';

export interface SlidePaneProps {
  // Identifies WHAT is being shown. When it changes, the new content is pushed
  // in from the right; the first render never animates, because arriving
  // somewhere is not the same as moving on from it.
  slideKey: string | null;
  children: ReactNode;
  className?: string;
  // WHICH WAY the next key change is going. Forward content arrives from the
  // right, as a stack pushes; a retraced step arrives from the LEFT, because a
  // back that comes in from the same side as the thing it undoes reads as
  // another step forward. Read at the moment the key changes, so the caller
  // sets it in the same update that navigates.
  dir?: 'forward' | 'back';
}

// Forward navigation inside a panel: the incoming content enters from the right,
// the way a stack pushes. Used for both lateral moves in the app — swapping one
// booking for another inside the modal, and arriving at a record from a search
// result — so the two cannot end up feeling like different gestures.
//
// The phase is set DURING RENDER, from the key, rather than in an effect. That
// is deliberate and is the same rule the drawers follow (lib/useSlide): a class
// assigned afterwards lands one commit late, and the panel paints once at its
// destination before snapping back to start — a flash, then the slide. Deriving
// it from the key puts the class in the very first painted frame of the new
// content. It clears on `animationend`, never on a stopwatch, so a slow frame
// cannot cut the movement short.
export function SlidePane({ slideKey, children, className, dir = 'forward' }: SlidePaneProps) {
  // `shown` is the key on screen; `from` is the last REAL one. They differ
  // because the key passes through null while the next record is fetched — and
  // comparing against `shown` alone meant every move looked like a first
  // arrival (null → B) and never animated.
  const [state, setState] = useState<{
    shown: string | null;
    from: string | null;
    moving: '' | 'forward' | 'back';
  }>({ shown: slideKey, from: slideKey, moving: '' });

  if (state.shown !== slideKey) {
    // A render-phase update: React re-renders immediately, before painting, so
    // the new content and its animation class commit together.
    setState((c) =>
      slideKey === null
        ? { shown: null, from: c.from, moving: '' }
        : {
            shown: slideKey,
            from: slideKey,
            moving: c.from !== null && c.from !== slideKey ? dir : '',
          },
    );
  }
  const moving = state.moving;

  const onAnimationEnd = useCallback((e: AnimationEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    setState((c) => (c.moving ? { ...c, moving: '' } : c));
  }, []);

  return (
    <div
      className={
        'slide-pane' +
        (moving === 'forward' ? ' is-pushing' : moving === 'back' ? ' is-popping' : '') +
        (className ? ' ' + className : '')
      }
      // What is on screen, so a test can see a move happen rather than infer it
      // from a class that may already have cleared.
      data-slide-key={slideKey ?? ''}
      onAnimationEnd={onAnimationEnd}
    >
      {children}
    </div>
  );
}
